import { Injectable } from '@angular/core';
// Librería SweetAlert2
import Swal, { SweetAlertIcon, SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class SwalService {
  // Configuración del notification

  private notifications: SweetAlertResult[] = []; // Arreglo para almacenar las notificaciones activas


  notification = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    onOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    },
  });

  constructor(private router: Router) { }

  /**
   * Función para disparar un modal de éxito personalizable.
   * @param message mensaje a mostrar
   */
  showSuccessModal(msgTitle, message?) {
    return Swal.fire({
      title: msgTitle,
      text: message,
      icon: 'success',
      heightAuto: false,
      onClose: () => { },
    });
  }

  /**
   * Función para disparar un modal de error personalizable.
   * @param msgTitle Título del mensaje de error
   * @param message mensaje de error a mostrar
   */
  showErrorModal(msgTitle, message?) {
    return Swal.fire({
      title: msgTitle,
      text: message,
      icon: 'error',
      heightAuto: false,
      onClose: () => { },
    });
  }

  /**
   * Función para disparar un modal de alerta personalizable.
   * @param msgTitle Título del mensaje de alerta
   * @param message mensaje a mostrar
   */
  showWarningModal(msgTitle, message?) {
    return Swal.fire({
      title: msgTitle,
      text: message,
      heightAuto: false,
      icon: 'warning',
      onClose: () => { },
    });
  }

  async showSuccessNotificationSocket(title: string, description?: string, orderId?: string) {
    const options: SweetAlertOptions = {
      title: title,
      text: description,
      icon: 'success' as SweetAlertIcon,
      position: 'top-end',
      showCancelButton: true, // Mostrar botón de cancelar
      confirmButtonText: 'Ir', // Cambiar texto del botón a "Ir"
      cancelButtonText: 'X', // Texto para el botón de cerrar
      toast: true,
      timer: null,
      customClass: {
        popup: 'notification-popup',
        confirmButton: 'notification-button', // Estilo para el botón de "Ir"
        cancelButton: 'cancel-button', // Estilo para el botón de "Cerrar"
      },
    };

    // Mostrar la notificación
    const notification: SweetAlertResult = await Swal.fire(options);

    // Almacenar la notificación activa
    this.notifications.push(notification);

    // Manejar el clic en el botón de "Ir" o en la notificación
    if (notification.value) {
      // Si se presiona el botón "Ir", redirigir a la vista con el orderId
      this.router.navigate(['/dashboard/reembolso'], { queryParams: { order_id: orderId } });
    }

    // Manejar el clic en el botón "Cerrar" (cancelar)
    if (notification.dismiss) {
      // Si se cierra, simplemente se elimina de la lista
      this.notifications = this.notifications.filter(n => n !== notification);
    }
  }




  /**
   * Función para disparar una notificación personalizable.
   * @param msgTitle Título del mensaje de alerta
   * @param message mensaje a mostrar
   */
  showSuccessNotification(msgTitle, message?) {
    return this.notification.fire({
      title: msgTitle,
      text: message,
      icon: 'success',
    });
  }

  /**
   * Función para disparar una notificación personalizable.
   * @param msgTitle Título del mensaje de alerta
   * @param message mensaje a mostrar
   */
  showWarningNotification(msgTitle, message?) {
    return this.notification.fire({
      title: msgTitle,
      text: message,
      icon: 'warning',
    });
  }

  /**
   * Función para disparar una notificación personalizable.
   * @param msgTitle Título del mensaje de alerta
   * @param message mensaje a mostrar
   */
  showErrorNotification(msgTitle: string, message?: string) {
    return this.notification.fire({
      title: msgTitle,
      text: message,
      icon: 'error',
    });
  }

  /**
   * Función para disparar un modal de confirmación personalizable.
   * Dependiendo de la opción seleccionada "Confirmar" o "Cancelar"
   * pueden realizar alguna acción llamando al método .then tras la respuesta de esta función.
   *
   * @param msgTitle Título de la pregunta para el usuario
   * @param message Un mensaje, puede dejarse como string vacío
   * @param acceptText Texto del botón de "confirmar"
   * @param cancelText Texto en el botón de "cancelar"
   * @returns Un objeto de tipo Promise<> tras la acción del usuario.
   *
   * Si pulsa "Confirm", el json que se obtendrá será de la forma {result: true}.
   * Si pulsa "Cancel", el JSON que se obtendrá será {dissmiss: value}, siendo value la forma de cancelación, y para este caso: "cancel"
   */
  showStyledConfirmationModal(msgTitle, message, acceptText, cancelText) {
    return Swal.fire({
      title: msgTitle,
      text: message,
      icon: 'question',
      confirmButtonText: acceptText,
      showCancelButton: true,
      cancelButtonText: cancelText,
      heightAuto: false,
      /**
       * Si se quieren estilos personalizados y usar clases
       * descomentar las líneas de abajo
       */
      // buttonsStyling: false,
      // customClass: {
      //   confirmButton: 'btn btn-primary',
      //   cancelButton: 'btn btn-danger',
      // },
    });
  }


  /**
   * Mostrar un modal alerta en que permite escribir en un input
   */
  showAlertModalInput(textTitle) {
    return Swal.fire({
      title: textTitle,
      input: 'text',
      inputAttributes: {
        autocapitalize: 'off'
      },
      showCancelButton: true,
      confirmButtonText: 'Enviar',
      showLoaderOnConfirm: true,
    })
  }

  /**
   * Función que retorna un string con la lista de errores encontrados tras una petición, que son enviados desde el back-end.
   * @param httpError Error atrapado por el exception de alguna petición.
   */
  getRequestErrorMessage(error: any) {
    let errorMessages = '';
    error.forEach((element: any) => {
      errorMessages += '- ' + element + '\n';
    });
    return errorMessages;
  }

  errorHandler(error: HttpErrorResponse) {
    if (error.status == 401) {
      localStorage.clear();
      this.router.navigate(['login']);
    } else if (error.status == 403) {
      this.showErrorModal(
        '¡Ocurrió un error!',
        this.getRequestErrorMessage(error.error.response.error)
      );
      this.router.navigate(['dashboard/perfil']);
    } else {
      return this.showErrorModal(
        '¡Ocurrió un error!',
        'Por favor, recarga la página o contacta con soporte'
        // error.error.response.error
        //   ? this.getRequestErrorMessage(error.error.response.error)
        //   : ''
      ).then((result: any) => {
        console.log(result);
        if (result.value) {
        }
      });
    }
  }
}
